import { BLOG_CATEGORIES, BLOG_STATUSES, getBlogCategoryLabel, getBlogStatusMeta } from '@/constants/blog';
import type { BlogPost } from '@/types/blog';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

const formatDate = (date?: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface BlogTableProps {
  posts: BlogPost[];
  loading: boolean;
  pagination: TablePaginationConfig;
  rowSelection?: object;
  onChange: (pagination: TablePaginationConfig, filters: unknown, sorter: unknown) => void;
  onEdit: (post: BlogPost) => void;
  onDelete: (id: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const BlogTable = ({
  posts,
  loading,
  pagination,
  rowSelection,
  onChange,
  onEdit,
  onDelete,
  sortBy,
  sortOrder,
}: BlogTableProps) => {
  const columns: ColumnsType<BlogPost> = [
    {
      title: 'Cover',
      key: 'image_url',
      width: 76,
      render: (_, record) =>
        record.image_url ? (
          <a
            href={record.image_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open cover image"
            className="inline-flex"
          >
            <img
              src={record.image_url}
              alt={record.title}
              className="w-16 h-10 object-cover rounded border border-gray-200"
              onError={e => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </a>
        ) : (
          <div className="w-16 h-10 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-[10px] text-gray-400">
            —
          </div>
        ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 260,
      ellipsis: true,
      sorter: true,
      sortOrder: sortBy === 'title' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (title: string, record) => (
        <Tooltip title={record.slug}>
          <span className="font-medium">{title}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (category: string) => <Tag color="blue">{getBlogCategoryLabel(category)}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const meta = getBlogStatusMeta(status);
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Published',
      dataIndex: 'published_at',
      key: 'published_at',
      width: 130,
      sorter: true,
      sortOrder: sortBy === 'published_at' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 130,
      sorter: true,
      sortOrder: sortBy === 'created_at' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => onEdit(record)} size="small" />
          </Tooltip>
          <Popconfirm
            title="Delete blog post"
            description="This will soft-delete the post."
            onConfirm={() => onDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table<BlogPost>
      columns={columns}
      dataSource={posts}
      loading={loading}
      pagination={pagination}
      rowSelection={rowSelection}
      onChange={onChange}
      rowKey="id"
      scroll={{ x: 1100 }}
      className="blog-table"
    />
  );
};

export { BLOG_CATEGORIES, BLOG_STATUSES };
export default BlogTable;
